import { memo, ReactNode, useCallback, useState } from 'react'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Collapse, InputLabel, Stack, Typography, useTheme } from '@mui/material'

type Props = {
  title: string
  description?: string
  children: ReactNode
  expanded?: boolean
  defaultExpanded?: boolean
  onChange?: (expanded: boolean) => void
  width?: number | string
  actions?: ReactNode
  error?: string | boolean
}

export const SimpleCollapsible = memo(
  ({
    title,
    description,
    children,
    defaultExpanded,
    expanded,
    onChange,
    width,
    actions,
    error
  }: Props) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded || false)
    const internalExpanded = expanded ?? isExpanded
    const theme = useTheme()

    const handleToggle = useCallback(() => {
      setIsExpanded(!internalExpanded)
      onChange?.(!internalExpanded)
    }, [internalExpanded, onChange])

    return (
      <Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: error
              ? 'error.main'
              : ({ palette }) =>
                  palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
            borderRadius: 1,
            backgroundColor: 'background.paper',
            width: width
          }}
        >
          <Stack
            onClick={handleToggle}
            sx={{
              cursor: 'pointer',
              userSelect: 'none',
              border: 'none',
              backgroundColor: 'transparent',
              padding: 0,
              margin: 0,
              width: '100%',
              height: '34.71px',
              px: 1.25,
              py: 1,
              borderRadius: 1
            }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                flex: 1,
                minWidth: 0,
                mr: 1
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  flexShrink: 0
                }}
              >
                {title}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                    pb: '1px',
                    color: ({ palette }) =>
                      theme.palette.mode === 'light'
                        ? palette.text.secondary
                        : palette.text.disabled
                  }}
                >
                  {description}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
              {actions}
              <KeyboardArrowDownIcon
                color="action"
                role="button"
                sx={{
                  transform: internalExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                  flexShrink: 0,
                  borderRadius: '50%'
                }}
              />
            </Stack>
          </Stack>

          <Collapse in={internalExpanded}>
            <Box
              sx={{
                py: 1,
                px: 1.25,
                borderTop: '1px solid',
                borderColor: ({ palette }) =>
                  palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'
              }}
            >
              {children}
            </Box>
          </Collapse>
        </Box>
        {!!error && typeof error === 'string' && <InputLabel error>{error}</InputLabel>}
      </Box>
    )
  }
)
